import '@banno/jha-wc/src/sliders';
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { NavigationService } from '@treasury/core/navigation/navigation.service';
import { Holiday, HolidaysService } from '@treasury/domain/dates';
import { DeviceService } from '@treasury/domain/services/device/device.service';
import { TmDetailedError } from '@treasury/domain/shared';
import {
    Transaction,
    Transfer,
    TransferAccount,
    TransferService,
    TransferType,
} from '@treasury/domain/transfers';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import { arrowBackIcon } from '@treasury/presentation/assets/icons';
import { TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/forms/tm-form';
import '@treasury/presentation/components/forms/tm-form-row';
import '@treasury/presentation/components/forms/tm-slider';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-card';
import '@treasury/presentation/components/tm-combo-box';
import '@treasury/presentation/components/tm-currency-field';
import '@treasury/presentation/components/tm-date-picker';
import '@treasury/presentation/components/tm-fdic-digital-sign';
import '@treasury/presentation/components/tm-labeled-list';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-select';
import { formatUsdCurrency } from '@treasury/utils/functions/currency.helpers';
import { GridColumnBodyLitRenderer, columnBodyRenderer } from '@vaadin/grid/lit.js';
import { format } from 'date-fns';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import {
    DatePickerDate,
    inactiveHolidays,
    inactiveWeekends,
} from '../../../utilities/disabled-date.functions';

export const tagName = 'transfer-payment-widget';
@customElement(tagName)
export class TransferPaymentWidget extends TmBaseComponent {
    @property({ type: Boolean })
    public loading = false;

    @InjectProperty()
    declare private transferService: TransferService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @InjectProperty()
    declare private navigationService: NavigationService;

    @InjectProperty()
    declare private deviceService: DeviceService;

    @DeepReactive()
    private transferPayment = new Transfer();

    @InjectProperty()
    declare private holidaysService: HolidaysService;

    @state()
    private transferPaymentFromAccounts: TransferAccount[] = [];

    @state()
    private transferPaymentFromAccountOptions: { value: TransferAccount; label: string }[] = [];

    @state()
    private selectedFromAccount: TransferAccount | undefined;

    @state()
    private transferPaymentToAccounts: TransferAccount[] = [];

    @state()
    private transferPaymentToAccountOptions: { value: TransferAccount; label: string }[] = [];

    @state()
    private selectedToAccount: TransferAccount | undefined;

    @state()
    private amount: number | undefined = 0;

    @state()
    private paymentDate: string | undefined;

    @state()
    private holidays: Array<Holiday> = [];

    @state()
    private confirming = false;

    @state()
    private hideHeader = false;

    @state()
    private paymentDateValid = false;

    @state()
    private updatingFromAccounts = false;

    @state()
    private updatingToAccounts = false;

    private get headerText() {
        const header = this.confirming
            ? `Transfer Payment ${this.status}`
            : 'Quick Transfer Payment';
        return this.hideHeader ? undefined : header;
    }

    private get status() {
        const [transaction] = this.transferPayment.transactions;
        return transaction.status === 'PendingApproval'
            ? 'Pending Approval'
            : transaction.status || 'Unknown';
    }

    private get isDesktop() {
        return this.deviceService.isDesktop;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.createDefaultTransferPayment();
    }

    protected async firstUpdated() {
        this.loading = true;
        Promise.all([this.getFromAccounts(), this.getToAccounts()]).then(() => {
            this.loading = false;
        });
    }

    protected createDefaultTransferPayment() {
        this.transferPayment = new Transfer();
        this.transferPayment.transactions = [
            ...this.transferPayment.transactions,
            new Transaction(),
        ];
    }

    protected render() {
        const [transaction] = this.transferPayment.transactions;
        return html`
            <tm-card .header=${this.headerText}>
                ${this.loading ? html`<tm-loader card></tm-loader>` : nothing}
                ${!this.loading && !this.confirming
                    ? html`<jha-slider class="-mx-4">
                          <jha-slider-content .staticHeight=${true} .fullHeight=${true}>
                              <jha-slider-pane pane-id="from-accounts-list">
                                  <div class="ml-4">
                                      <div class="flex justify-center items-center relative my-3">
                                          <tm-icon
                                              class="absolute left-0"
                                              .icon=${arrowBackIcon}
                                              @click=${() => {
                                                  this.changePane('index');
                                              }}
                                          ></tm-icon>
                                          <span class="font-medium">Select from account</span>
                                      </div>
                                      ${this.renderFromAccountsList()}
                                  </div>
                              </jha-slider-pane>
                              <jha-slider-pane pane-id="to-accounts-list">
                                  <div class="ml-4">
                                      <div class="flex justify-center items-center relative my-3">
                                          <tm-icon
                                              class="absolute left-0"
                                              .icon=${arrowBackIcon}
                                              @click=${() => {
                                                  this.changePane('index');
                                              }}
                                          ></tm-icon>
                                          <span class="font-medium"
                                              >Select to transfer account</span
                                          >
                                      </div>
                                      ${this.renderToAccountsList()}
                                  </div>
                              </jha-slider-pane>
                              <jha-slider-pane index focus>
                                  <tm-form class="">
                                      <tm-fdic-digital-sign
                                          container="py-2"
                                          class="ml-4"
                                          .reversed=${this.theme === 'dark'}
                                      ></tm-fdic-digital-sign>
                                      ${this.renderFromAccountSelection()}
                                      ${this.renderToAccountSelection()}
                                      <tm-form-row required label="Amount">
                                          <tm-currency-field
                                              class="max-w-[130px] my-2"
                                              .initial=${this.amount}
                                              .required=${true}
                                              .maxLength=${15}
                                              .invalid=${!this.amount}
                                              @value-changed=${(e: TmValueChanged<string>) => {
                                                  const { value } = e.detail;
                                                  if (!value) {
                                                      return;
                                                  }

                                                  this.amount = parseFloat(value);
                                                  this.transferPayment.transactions[0].amount =
                                                      this.amount;
                                              }}
                                          ></tm-currency-field>
                                      </tm-form-row>
                                      <tm-form-row required label="Payment date" noWrap>
                                          <tm-date-picker
                                              class="max-w-[150px]"
                                              .required=${true}
                                              .min=${new Date()}
                                              .isDateDisabled=${(date: DatePickerDate) =>
                                                  inactiveWeekends(date) ||
                                                  inactiveHolidays(date, this.holidays)}
                                              .initial=${new Date()}
                                              @value-changed=${(e: TmValueChanged<Date>) => {
                                                  const { value, isValid } = e.detail;
                                                  this.paymentDate = value?.toDateString();
                                                  this.paymentDateValid = isValid;
                                                  this.transferPayment.transactions[0].transferDate =
                                                      value ? value?.toDateString() : '';
                                              }}
                                          ></tm-date-picker>
                                      </tm-form-row>
                                  </tm-form>
                                  <div
                                      class=${classMap({
                                          'flex w-full items-center justify-around pt-4': true,
                                          'flex-col': this.isDesktop,
                                      })}
                                  >
                                      <tm-button
                                          importance="secondary"
                                          .size=${this.isDesktop ? 'normal' : 'small'}
                                          .disabled=${this.loading ||
                                          !this.selectedFromAccount ||
                                          !this.selectedToAccount ||
                                          !this.amount ||
                                          Number(this.amount) > 9999999999999 ||
                                          !this.paymentDate ||
                                          !this.paymentDateValid}
                                          @click=${() => {
                                              this.submitTransferPayment();
                                          }}
                                          >Make Transfer Payment</tm-button
                                      >
                                      <tm-button
                                          importance="tertiary"
                                          .size=${this.isDesktop ? 'normal' : 'small'}
                                          @click=${() => {
                                              this.navigationService.navigate(
                                                  `transfers/payment/create/new`
                                              );
                                          }}
                                          >Advanced transfer options</tm-button
                                      >
                                  </div></jha-slider-pane
                              ></jha-slider-content
                          ></jha-slider
                      >`
                    : nothing}
                ${this.confirming
                    ? html`<div>
                          <h2 class="text-lg font-medium">Transfer Payment</h2>
                          <h1 class="text-2xl">${formatUsdCurrency(transaction.amount || 0)}</h1>
                          <div class="grid grid-cols-[auto_1fr] gap-x-3 my-4 text-sm">
                              <div class="text-left mr-3 py-1 text-secondary">From</div>
                              <div class="text-left py-1 text-primary">
                                  ${transaction.fromAccount?.accountDisplayLabel}
                              </div>
                              <div class="text-left mr-3 py-1 text-secondary">To</div>
                              <div class="text-left py-1 text-primary">
                                  ${transaction.toAccount?.accountDisplayLabel}
                              </div>
                              <div class="text-left mr-3 py-1 text-secondary">Frequency</div>
                              <div class="text-left py-1 text-primary">One Time</div>
                              <div class="text-left mr-3 py-1 text-secondary">Status</div>
                              <div class="text-left py-1 text-primary">${this.status}</div>
                              <div class="text-left mr-3 py-1 text-secondary">Payment Date</div>
                              <div class="text-left py-1 text-primary">
                                  ${format(new Date(transaction.transferDate || ''), 'P')}
                              </div>
                              <div class="text-left mr-3 py-1 text-secondary">Transaction ID</div>
                              <div class="text-left py-1 text-primary">
                                  ${transaction.transactionId}
                              </div>
                          </div>
                          <div class="flex w-full items-center justify-center">
                              <tm-button
                                  importance="secondary"
                                  @click=${() => {
                                      this.confirming = false;
                                      this.createDefaultTransferPayment();
                                      this.amount = 0;
                                  }}
                                  >Done</tm-button
                              >
                          </div>
                      </div>`
                    : nothing}
            </tm-card>
        `;
    }

    protected renderFromAccountSelection() {
        if (this.deviceService.isDesktop) {
            const helperText = this.selectedFromAccount
                ? `Balance: ${formatUsdCurrency(this.selectedFromAccount.availableBalance || 0)}`
                : '';
            return html`<tm-form-row required noWrap .label=${'From'}
                ><tm-combo-box
                    class="max-w-[200px] my-2"
                    .placeholder=${'Select account'}
                    .helperText=${helperText}
                    .items=${this.transferPaymentFromAccountOptions}
                    .disabled=${this.updatingFromAccounts}
                    @value-changed=${(e: CustomEvent) => {
                        const selectedAccount = e.detail as TransferAccount;
                        this.selectedFromAccount = selectedAccount;
                        this.transferPayment.transactions[0].fromAccount = selectedAccount;
                        this.updateAccountLists(selectedAccount.id, 'To');
                    }}
                ></tm-combo-box
            ></tm-form-row>`;
        }
        return html`<tm-form-row required noWrap .label=${'From'}>
            <tm-slider
                no-sheet
                class="cursor-pointer"
                .value=${this.renderAccount(
                    this.transferPayment.transactions[0].fromAccount || ({} as TransferAccount)
                )}
                @click=${() => {
                    this.changePane('from-accounts-list');
                }}
                .placeholder=${'Select account'}
            >
            </tm-slider
        ></tm-form-row>`;
    }

    protected renderToAccountSelection() {
        if (this.deviceService.isDesktop) {
            const helperText = this.selectedToAccount
                ? `Balance: ${formatUsdCurrency(this.selectedToAccount.currentBalance || 0)}`
                : '';
            return html`<tm-form-row required noWrap .label=${'To transfer'}
                ><tm-combo-box
                    class="max-w-[200px] my-2"
                    .items=${this.transferPaymentToAccountOptions}
                    .placeholder=${'Select account'}
                    .helperText=${helperText}
                    .disabled=${this.updatingToAccounts}
                    @value-changed=${(e: CustomEvent) => {
                        const selectedAccount = e.detail as TransferAccount;
                        this.selectedToAccount = selectedAccount;
                        this.transferPayment.transactions[0].toAccount = selectedAccount;
                        this.updateAccountLists(selectedAccount.id, 'From');
                    }}
                ></tm-combo-box
            ></tm-form-row>`;
        }
        return html`<tm-form-row required noWrap .label=${'To transfer'}>
            <tm-slider
                no-sheet
                class="cursor-pointer"
                .value=${this.renderAccount(
                    this.transferPayment.transactions[0].toAccount || ({} as TransferAccount)
                )}
                .placeholder=${'Select account'}
                @click=${() => {
                    this.changePane('to-accounts-list');
                }}
            >
            </tm-slider>
        </tm-form-row>`;
    }

    protected async getFromAccounts() {
        this.transferPaymentFromAccounts = await this.transferService.getTransferFromAccounts();
        this.transferPaymentFromAccountOptions = this.transferPaymentFromAccounts.map(account => ({
            value: account,
            label: account.accountDisplayLabel,
        }));
        this.holidays = await this.holidaysService.getHolidays();
    }

    protected async getToAccounts() {
        this.transferPaymentToAccounts = await this.transferService.getTransferToAccounts(
            this.selectedFromAccount?.id || 0
        );
        this.transferPaymentToAccountOptions = this.transferPaymentToAccounts.map(account => ({
            value: account,
            label: account.accountDisplayLabel,
        }));
    }

    protected async updateAccountLists(id: number, type: string) {
        this.updatingFromAccounts = type === 'From';
        this.updatingToAccounts = type === 'To';
        const response = await this.transferService.getFilteredTransferAccounts(id, type);
        const accountList = response
            .filter(acc => acc.id !== id)
            .map(account => ({
                value: account,
                label: account.accountDisplayLabel,
            }));
        if (this.updatingFromAccounts) {
            this.transferPaymentFromAccountOptions = accountList;
            this.updatingFromAccounts = false;
        }
        if (this.updatingToAccounts) {
            this.transferPaymentToAccountOptions = accountList;
            this.updatingToAccounts = false;
        }
    }

    protected async submitTransferPayment() {
        this.loading = true;
        this.transferPayment.type = TransferType.OneToOne;
        try {
            const response = await this.transferService.createTransfer(this.transferPayment);
            this.transferPayment = response;
            this.dispatchEvent(new CustomEvent('transfer-success'));
            if (this.status === 'Pending Approval') {
                this.notificationService.renderInfo('Transfer is pending approval');
            } else {
                this.notificationService.renderSuccess('Transfer successful');
            }
            this.confirming = true;
        } catch (e) {
            this.notificationService.renderError(e as TmDetailedError);
        } finally {
            this.loading = false;
        }
    }

    private changePane(paneId: string) {
        this.hideHeader = paneId === 'from-accounts-list' || paneId === 'to-accounts-list';
        const sliderContent = this.shadowRoot?.querySelector('jha-slider-content');
        if (sliderContent) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (sliderContent as any).focusedPane = paneId;
        }
    }

    private renderAccount(account: TransferAccount) {
        const amount = account.type === 'Loan' ? account.currentBalance : account.availableBalance;
        if (account.accountDisplayLabel) {
            return html`<div class="flex flex-col items-end">
                <div class="text-sm text-action">${account.accountDisplayLabel}</div>
                <div class="text-xs text-secondary">${formatUsdCurrency(amount || 0)}</div>
            </div>`;
        } else {
            return undefined;
        }
    }

    private renderFromAccountsList() {
        const renderAccount = (item: TransferAccount) =>
            html`<div class="py-2 pr-4">
                <div class="flex flex-row justify-between">
                    <div class="flex flex-col">
                        <div class="text-sm">${item.name}</div>
                        <div class="text-xs text-secondary">${item.accountDisplayLabel}</div>
                    </div>
                    <div class="flex flex-col text-right">
                        <div class="text-sm">${formatUsdCurrency(item.availableBalance || 0)}</div>
                        <div class="text-xs text-secondary">Available</div>
                    </div>
                </div>
            </div>`;
        return html`<vaadin-grid
                .items=${this.transferPaymentFromAccounts}
                @active-item-changed=${(e: CustomEvent) => {
                    if (!e.detail.value) {
                        return;
                    }
                    this.selectedFromAccount = e.detail.value;
                    this.transferPayment.transactions[0].fromAccount = e.detail.value;
                    this.getToAccounts();
                    this.changePane('index');
                }}
                ><vaadin-grid-column
                    ${columnBodyRenderer(renderAccount as GridColumnBodyLitRenderer<TransferAccount>)}
                ></vaadin-grid-column
                ></vaadin-grid-column
            ></vaadin-grid>`;
    }

    private renderToAccountsList() {
        const renderAccount = (item: TransferAccount) =>
            html`<div class="py-2 pr-4">
                <div class="flex flex-row justify-between">
                    <div class="flex flex-col">
                        <div class="text-sm">${item.name}</div>
                        <div class="text-xs text-secondary">${item.number} | ${item.status}</div>
                    </div>
                    <div class="flex flex-col text-right">
                        <div class="text-sm">${formatUsdCurrency(item.currentBalance || 0)}</div>
                        <div class="text-xs text-secondary">Current</div>
                    </div>
                </div>
            </div>`;
        return html`<vaadin-grid
            .items=${this.transferPaymentToAccounts}
            @active-item-changed=${(e: CustomEvent) => {
                if (!e.detail.value) {
                    return;
                }
                this.selectedToAccount = e.detail.value;
                this.transferPayment.transactions[0].toAccount = e.detail.value;
                this.changePane('index');
            }}
            ><vaadin-grid-column
                ${columnBodyRenderer(renderAccount as GridColumnBodyLitRenderer<TransferAccount>)}
            ></vaadin-grid-column
            >/vaadin-grid></vaadin-grid
        >`;
    }

    public static get styles() {
        return css`
            :host {
                display: block;
                --tm-card-min-height: 100px;
            }
            vaadin-grid,
            tm-loader {
                height: 270px;
            }
            vaadin-grid-cell-content {
                padding: 0;
            }
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TransferPaymentWidget;
    }
}
